import { PropsWithChildren, ReactNode, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import LoadingElement from "../../components/loading-element/loading-element";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { WarnButton } from "../../components/Buttons/SecondaryButton";
import { CustomTextArea } from "../../components/Input/CustomInput";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { capitalize } from "../../shared/functions/casing";
import { useErrorToast, withToasts } from "../../components/Toast/Toast";
import {
  ApprovalType,
  shouldShowApprovalButtons,
} from "../../../core/models/entities/BaseLogo";
import { ApprovalGroup } from "../../../core/models/entities/Approval";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../components/LogoButton/IconButton";
import { LogoPdfGenerator } from "../../../utils/functions/pdf-generator.service";
import { usePublicApiClient } from "../../hooks/usePublicApiClient";
import { Design } from "../../../core/models/entities/Design";
import { mapToLogo } from "../../../data/models/mappers/mapLogo";
import { mapToDesign } from "../../../data/models/mappers/mapDesign";

enum ApprovalFlowState {
  Approving,
  Loading,
  End,
}

interface DetailTableProps {
  fields: Record<string, ReactNode>;
}
export const DetailTable = ({ fields }: DetailTableProps) => (
  <div className="flex flex-col gap-y-3 mb-6">
    {Object.keys(fields).map((key) =>
      !fields[key] ? undefined : (
        <div key={key} className="flex flex-row">
          <strong className="mr-1">{key}:</strong> {fields[key]}
        </div>
      )
    )}
  </div>
);

interface ExternalApprovalFlowProps {
  title: string;
  placeVerdict: (approval: boolean, note?: string) => Promise<any>;
  subject: string;
  readOnly?: boolean;
  isEditable?: boolean;
  approvalType?: ApprovalType;
  approval?: ApprovalGroup | null;
  view: ReactNode;
  details: ReactNode;
  carousel?: ReactNode;
  isMultiApproval?: boolean;
  allItemsCompleted?: boolean;
  onViewItemsAgain?: () => void;
}

export function ExternalApprovalFlow({
  placeVerdict,
  title,
  details,
  view,
  subject,
  readOnly,
  isEditable,
  approvalType,
  approval,
  carousel,
  isMultiApproval = false,
  allItemsCompleted = false,
  onViewItemsAgain,
}: ExternalApprovalFlowProps) {
  const [qp] = useSearchParams();

  // Get approval keys directly from URL parameters (code for single, keys for multi)
  const code = qp.get("code");
  const keys = qp.get("keys");
  const approvalKeys = keys ? keys.split(",") : code ? [code] : [];

  // If all items are completed on initial load, show the approval view instead of thank you
  const [state, setState] = useState<ApprovalFlowState>(
    isMultiApproval && allItemsCompleted
      ? ApprovalFlowState.Approving
      : ApprovalFlowState.Approving
  );
  const [showThankYou, setShowThankYou] = useState(false);

  const handleApproval = async (approved: boolean, note?: string) => {
    setState(ApprovalFlowState.Loading);
    try {
      await placeVerdict(approved, note);
      // For multi-approval, only show thank you if all items are completed
      if (isMultiApproval && !allItemsCompleted) {
        setState(ApprovalFlowState.Approving);
      } else {
        setState(ApprovalFlowState.Approving);
        setShowThankYou(true);
      }
    } catch (error) {
      console.error("Failed to place verdict:", error);
      // Reset to approving state so user can try again
      setState(ApprovalFlowState.Approving);
    }
  };

  const handleViewItemsAgain = () => {
    setShowThankYou(false);
    if (onViewItemsAgain) {
      onViewItemsAgain();
    }
  };

  // NOTE: The note is only used with rejections, even though the user can type one and approve.
  const approve = () => handleApproval(true);
  const reject = (note: string) => handleApproval(false, note);

  // Show ThankYouFrame when user completes an approval or when explicitly requested
  if (
    showThankYou ||
    (isMultiApproval && allItemsCompleted && !onViewItemsAgain)
  ) {
    return (
      <ThankYouFrame
        onViewItemsAgain={isMultiApproval ? handleViewItemsAgain : undefined}
      />
    );
  }
  if (state === ApprovalFlowState.Loading) return <LoadingFrame />;

  return (
    <ApprovalFrame
      carousel={carousel}
      subject={subject}
      title={title}
      approvalKeys={approvalKeys}
      isMultiApproval={isMultiApproval}
    >
      <BoxContent className="relative text-center h-full flex-1 !p-0">
        <div className="absolute top-4 left-4 text-md">
          <span className="font-bold">{capitalize(subject)}</span> - {title}
        </div>

        <div className="flex justify-between h-full flex-1">
          <div
            className={`w-full flex flex-col flex-1 justify-center items-center text-center ${
              isEditable === false ? "py-20" : "p-20"
            }`}
          >
            {view}
          </div>
        </div>
      </BoxContent>

      <ApprovalSidebar>
        {details}
        {(() => {
          // Simple logic: only check if the specific approval type has pending status
          let shouldShowButtons = false;

          if (approvalType !== undefined && approval) {
            if (approvalType === 0 && approval.customer) {
              // Customer approval
              shouldShowButtons = approval.customer.status === 0; // Pending
            } else if (approvalType === 1 && approval.external) {
              // External approval
              shouldShowButtons = approval.external.status === 0; // Pending
            }
          }

          return (
            shouldShowButtons && (
              <ApprovalArea onReject={reject} onApprove={approve} />
            )
          );
        })()}
      </ApprovalSidebar>
    </ApprovalFrame>
  );
}

interface ApprovalFrameProps extends PropsWithChildren {
  carousel?: ReactNode;
  subject?: string;
  title?: string;
  approvalKeys?: string[];
  isMultiApproval?: boolean;
}

function ApprovalFrame({
  children,
  carousel,
  subject,
  title,
  approvalKeys,
  isMultiApproval,
}: ApprovalFrameProps) {
  const { getLogosForPdf, getDesignsForPdf } = usePublicApiClient();
  const [isDownloading, setIsDownloading] = useState(false);

  // Helper function to find the most common vendor logo URL
  const findMostCommonVendorLogoUrl = (
    items: Array<{ vendorLogoUrl?: string }>
  ): string | undefined => {
    const logoUrlCounts: Record<string, number> = {};

    // Count occurrences of each vendor logo URL
    items.forEach((item) => {
      if (item.vendorLogoUrl) {
        logoUrlCounts[item.vendorLogoUrl] =
          (logoUrlCounts[item.vendorLogoUrl] || 0) + 1;
      }
    });

    // Find the URL with the highest count
    let mostCommonUrl: string | undefined;
    let highestCount = 0;

    for (const [url, count] of Object.entries(logoUrlCounts)) {
      if (count > highestCount) {
        highestCount = count;
        mostCommonUrl = url;
      }
    }

    return mostCommonUrl;
  };

  const handleDownloadPdf = async () => {
    if (!approvalKeys || approvalKeys.length === 0) {
      console.error("No approval keys available for PDF download");
      return;
    }

    async function transaction() {
      // Ensure approvalKeys is not undefined
      const keys = approvalKeys || [];

      if (subject === "logo") {
        // Download logos PDF
        const logos = await getLogosForPdf(keys);
        const mappedLogos = logos.map(mapToLogo);

        // Find the most common vendor logo URL among all logos
        const vendorLogoUrl = findMostCommonVendorLogoUrl(logos);

        await LogoPdfGenerator.generateMultipleLogosPdf(
          mappedLogos,
          vendorLogoUrl
        );
      } else if (subject === "design") {
        // Download designs PDF
        const designs = await getDesignsForPdf(keys);
        const mappedDesigns = designs.map(mapToDesign);

        // Find the most common vendor logo URL among all designs
        const vendorLogoUrl = findMostCommonVendorLogoUrl(designs);

        await LogoPdfGenerator.generateMultipleDesignsPdf(
          mappedDesigns,
          vendorLogoUrl
        );
      }
    }

    try {
      const isLogo = subject === "logo";
      await withToasts(
        transaction,
        isLogo
          ? "Generating logo PDF..."
          : "Generating design PDF. This might take a while...",
        isLogo
          ? "Logo PDF generated successfully!"
          : "Design PDF generated successfully!",
        isLogo
          ? "Failed to generate logo PDF"
          : "Failed to generate design PDF",
        setIsDownloading
      );
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Error downloading PDF:", error);
    }
  };

  return (
    <JetSportBox>
      <div className="flex justify-between items-center py-4 px-4 bg-white font-bold rounded-tr border-b-[1px] border-solid border-borderGray">
        <span>PREVIEW</span>
        <IconButton
          key={"download-button"}
          icon={<FontAwesomeIcon icon={faDownload} />}
          onClick={handleDownloadPdf}
          className="!h-9"
          disabled={isDownloading}
        >
          {isDownloading ? "DOWNLOADING..." : "DOWNLOAD PDF"}
        </IconButton>
      </div>

      {carousel && (
        <div className="px-4 py-3 bg-gray-100 border-b-[1px] border-solid border-borderGray">
          {carousel}
        </div>
      )}

      <div className="flex flex-row">{children}</div>
    </JetSportBox>
  );
}

function ApprovalSidebar({ children }: PropsWithChildren) {
  return (
    <div className="flex flex-col bg-gray-100 p-4 border-l-[1px]">
      <div className="flex flex-col justify-between h-full min-w-[400px]">
        {children}
      </div>
    </div>
  );
}

interface ApprovalAreaProps extends PropsWithChildren {
  onApprove: () => void;
  onReject: (note: string) => void;
}
function ApprovalArea({ onApprove, onReject }: ApprovalAreaProps) {
  const [note, setNote] = useState("");

  const handleReject = () => {
    if (note.trim() === "") {
      useErrorToast(
        "If you wish to reject this logo, please provide a brief reason. Your feedback is essential for us to take corrective action and ensure you receive the desired result.\n\n(Example: color deviation, incorrect size, text adjustment, other)"
      );
      return;
    }
    onReject(note);
  };

  return (
    <div>
      <div className="space-y-8 mb-4">
        <CustomTextArea
          className="h-[200px] bg-white border-borderGray"
          value={note}
          placeholder="Remark..."
          onChange={(x) => setNote(x.target.value)}
        />
      </div>
      <div className="flex space-x-8 justify-center">
        <PrimaryButton width="md" onClick={onApprove}>
          Approve
        </PrimaryButton>
        <WarnButton width="md" onClick={handleReject}>
          Reject
        </WarnButton>
      </div>
    </div>
  );
}

interface ThankYouFrameProps {
  onViewItemsAgain?: () => void;
}

export function ThankYouFrame({ onViewItemsAgain }: ThankYouFrameProps) {
  return (
    <JetSportBox>
      <BoxHeader2 />
      <BoxContent
        className={"items-center text-center pt-44 justify-between pb-32"}
      >
        <div className={"flex flex-col space-y-8"}>
          <FontAwesomeIcon
            size={"5x"}
            className={"text-status-success"}
            icon={faPaperPlane}
          />
          <span>
            <span className={"text-[32px]"}>Thank you for your time!</span>
            <br />
            <span className={"text-[18px]"}>
              Your input will be sent to the owner
            </span>
          </span>
          {onViewItemsAgain && (
            <PrimaryButton onClick={onViewItemsAgain} className="mt-6">
              View Items Again
            </PrimaryButton>
          )}
        </div>
      </BoxContent>
    </JetSportBox>
  );
}

export function LoadingFrame() {
  return (
    <JetSportBox>
      <BoxHeader2 />
      <BoxContent>
        <LoadingElement className={"rounded"} />
      </BoxContent>
      <BoxFooter />
    </JetSportBox>
  );
}

export function InvalidRoute() {
  return (
    <JetSportBox>
      <BoxHeader2 />
      <BoxContent>Invalid route</BoxContent>
      <BoxFooter />
    </JetSportBox>
  );
}
